import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    FormGroup,
    Stack,
    Typography,
} from '@mui/material';
import { useReplaceContractHospitalsMutation } from '../../../api/contractSlice';
import { useGetHospitalsInOrganizationQuery } from '../../../api/organizationSlice';
import { theme } from '../../../theme';

function ContractHospitals({ contract }) {
    const [isEditingHospitals, setIsEditingHospitals] = useState(false);
    const [selectedHospitalIds, setSelectedHospitalIds] = useState([]);
    const [updateContractHospitals, { isLoading: isSavingHospitals }] = useReplaceContractHospitalsMutation();

    const organizationId = contract?.organization_id;
    const {
        data: organizationHospitals,
        isLoading: isHospitalsLoading,
        isError: isHospitalsError,
    } = useGetHospitalsInOrganizationQuery(organizationId, { skip: !organizationId });

    const getHospitalIds = (hospitals = []) => hospitals.map((hospital) => hospital?.id).filter(Boolean);

    const orgHospitals = organizationHospitals || [];
    const contractHospitals = contract?.hospitals || [];
    const contractHospitalIds = useMemo(() => getHospitalIds(contractHospitals), [contractHospitals]);

    useEffect(() => {
        setSelectedHospitalIds(contractHospitalIds);
    }, [contractHospitalIds]);

    const currentHospitalNames = contractHospitals.map((hospital) => hospital?.name).filter(Boolean);
    const hasHospitalChanges =
        contractHospitalIds.length !== selectedHospitalIds.length ||
        contractHospitalIds.some((id) => !selectedHospitalIds.includes(id));

    const handleToggleHospital = (hospitalId) => {
        setSelectedHospitalIds((previousIds) => {
            if (previousIds.includes(hospitalId)) {
                return previousIds.filter((id) => id !== hospitalId);
            }
            return [...previousIds, hospitalId];
        });
    };

    const handleCancelHospitalEdit = () => {
        setSelectedHospitalIds(contractHospitalIds);
        setIsEditingHospitals(false);
    };

    const handleSaveHospitalEdit = async () => {
        await updateContractHospitals({
            contractId: contract?.id,
            hospitalIds: selectedHospitalIds,
        }).unwrap();

        setIsEditingHospitals(false);
    };

    if (isHospitalsLoading) {
        return (
            <Typography variant='normal' sx={{ fontSize: '14px', color: 'subtext.main' }}>
                Loading organization hospitals...
            </Typography>
        );
    } else if (isHospitalsError) {
        return (
            <Typography variant='normal' sx={{ fontSize: '14px', color: 'error.main' }}>
                Could not load hospitals for this organization.
            </Typography>
        );
    } else if (orgHospitals.length === 0) {
        return (
            <Typography variant='normal' sx={{ fontSize: '14px', color: 'subtext.main' }}>
                No hospitals found for this organization.
            </Typography>
        );
    }

    return (
        <Stack
            spacing={2}
            sx={{
                width: '100%',
                borderRadius: 2,
                boxShadow: 2,
                backgroundColor: theme.palette.background.white,
                p: 2,
            }}
        >
            <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <Typography variant='heavy' sx={{ fontSize: '16px' }}>
                    Facilities under contract
                </Typography>
                {!isEditingHospitals && (
                    <Button variant='outlined' size='small' onClick={() => setIsEditingHospitals(true)}>
                        Edit
                    </Button>
                )}
            </Stack>

            {currentHospitalNames.length > 0 ? (
                <Typography variant='normal' sx={{ fontSize: '14px' }}>
                    {currentHospitalNames.join(', ')}
                </Typography>
            ) : (
                <Typography variant='normal' sx={{ fontSize: '14px', color: 'subtext.main' }}>
                    No hospitals currently assigned.
                </Typography>
            )}

            {isEditingHospitals && (
                <>
                    <Divider />
                    <Stack spacing={1.5}>
                        <Typography variant='normal' sx={{ fontSize: '13px', color: 'subtext.main' }}>
                            Select hospitals in this organization
                        </Typography>

                        <FormGroup
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                gap: 0.5,
                                maxHeight: 260,
                                overflowY: 'auto',
                                overflowX: 'auto',
                                pr: 1,
                            }}
                        >
                            {orgHospitals.map((hospital) => {
                                const hospitalId = hospital?.id;
                                if (!hospitalId) return null;

                                return (
                                    <FormControlLabel
                                        key={hospitalId}
                                        sx={{
                                            mr: 0,
                                            alignItems: 'center',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '14px',
                                            },
                                        }}
                                        control={(
                                            <Checkbox
                                                size='small'
                                                checked={selectedHospitalIds.includes(hospitalId)}
                                                onChange={() => handleToggleHospital(hospitalId)}
                                            />
                                        )}
                                        label={hospital.name}
                                    />
                                );
                            })}
                        </FormGroup>

                        <Stack direction='row' spacing={1} justifyContent='flex-end'>
                            <Button variant='outlined' size='small' onClick={handleCancelHospitalEdit} disabled={isSavingHospitals}>
                                Cancel
                            </Button>
                            <Button
                                variant='blueContained'
                                size='small'
                                onClick={handleSaveHospitalEdit}
                                disabled={!hasHospitalChanges || isSavingHospitals}
                            >
                                {isSavingHospitals ? 'Saving...' : 'Save'}
                            </Button>
                        </Stack>
                    </Stack>
                </>
            )}
        </Stack>
    );
}

export default ContractHospitals;
